import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CardDetail,
  type ContentBlock,
  useAddBlock,
  useDeleteBlock,
  useMoveBlock,
} from '@/entities/card';
import { FormError } from '@/shared/forms';
import { stepMove } from '@/shared/lib/reorder';
import { Button, ConfirmDialog } from '@/shared/ui';

import { useCardAbilities } from './abilities';
import { ChecklistBlock } from './ChecklistBlock';
import styles from './Content.module.css';
import { MarkdownBlock } from './MarkdownBlock';

/** Ordered card content: Markdown and checklist blocks in any sequence. */
export function ContentBlocks({ card }: { card: CardDetail }) {
  const { t } = useTranslation();
  const { editContent } = useCardAbilities();
  const addBlock = useAddBlock(card.id);
  const moveBlock = useMoveBlock(card.id);
  const deleteBlock = useDeleteBlock(card.id);
  // A just-added markdown block opens in edit mode.
  const [editBlockId, setEditBlockId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ContentBlock | null>(null);
  const ids = card.content_blocks.map((block) => block.id);

  const add = (blockType: 'markdown' | 'checklist') => {
    addBlock.mutate(
      { block_type: blockType },
      {
        onSuccess: (updated) => {
          const created = updated.content_blocks.find((b) => !ids.includes(b.id));
          if (created) setEditBlockId(created.id);
        },
      },
    );
  };

  const move = (block: ContentBlock, direction: 'up' | 'down') => {
    const step = stepMove(ids, block.id, direction, 'block');
    if (step) moveBlock.mutate({ blockId: block.id, ...step });
  };

  return (
    <section className={styles.section} aria-labelledby={`content-${card.id}`}>
      <h3 id={`content-${card.id}`} className={styles.sectionTitle}>
        {t('content.title')}
      </h3>
      <FormError error={addBlock.error ?? moveBlock.error ?? deleteBlock.error} />
      {card.content_blocks.length === 0 && <p className={styles.empty}>{t('content.empty')}</p>}
      <ol className={styles.blocks}>
        {card.content_blocks.map((block, index) => (
          <li key={block.id} className={styles.block}>
            <div
              className={styles.blockBody}
              inert={!editContent && block.block_type === 'checklist' ? true : undefined}
            >
              {block.block_type === 'markdown' ? (
                <MarkdownBlock
                  card={card}
                  block={block}
                  startEditing={editBlockId === block.id}
                  onEditingDone={() => {
                    setEditBlockId(null);
                  }}
                />
              ) : (
                <ChecklistBlock
                  card={card}
                  block={block}
                  autoFocusTitle={editBlockId === block.id}
                />
              )}
            </div>
            {editContent && (
              <div
                className={styles.blockTools}
                role="group"
                aria-label={t('content.blockActions')}
              >
                <button
                  type="button"
                  className={styles.tool}
                  aria-label={t('content.moveUp')}
                  title={t('content.moveUp')}
                  disabled={index === 0 || moveBlock.isPending}
                  onClick={() => {
                    move(block, 'up');
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={styles.tool}
                  aria-label={t('content.moveDown')}
                  title={t('content.moveDown')}
                  disabled={index === ids.length - 1 || moveBlock.isPending}
                  onClick={() => {
                    move(block, 'down');
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className={`${styles.tool} ${styles.toolDanger}`}
                  aria-label={t('content.delete')}
                  title={t('content.delete')}
                  disabled={deleteBlock.isPending}
                  onClick={() => {
                    setDeleting(block);
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>
      {deleting && (
        <ConfirmDialog
          title={t('content.deleteTitle')}
          message={t(
            deleting.block_type === 'checklist' ? 'content.deleteChecklist' : 'content.deleteText',
          )}
          confirmLabel={t('common.delete')}
          pending={deleteBlock.isPending}
          onCancel={() => {
            setDeleting(null);
          }}
          onConfirm={() => {
            deleteBlock.mutate(deleting.id, {
              onSettled: () => {
                setDeleting(null);
              },
            });
          }}
        />
      )}
      {editContent && (
        <div className={styles.addBar}>
          <Button
            size="sm"
            disabled={addBlock.isPending}
            onClick={() => {
              add('markdown');
            }}
          >
            + {t('content.addText')}
          </Button>
          <Button
            size="sm"
            disabled={addBlock.isPending}
            onClick={() => {
              add('checklist');
            }}
          >
            + {t('content.addChecklist')}
          </Button>
        </div>
      )}
    </section>
  );
}
